import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";

/**
 * Carrier MCP business prompts (5).
 * Renamed from registerPrompts → registerAllPrompts for consistency.
 */
export function registerAllPrompts(server: McpServer): void {
  server.registerPrompt(
    "fleet_health_report",
    {
      title: "Fleet Health Report",
      description:
        "Generate a comprehensive fleet health report: account balances, eSIM status breakdown, low-balance alerts, and utilization rates.",
    },
    async () => ({
      messages: [
        {
          role: "user",
          content: {
            type: "text",
            text: `You are a Carrier fleet analyst. Generate a health report by:

1. Call list_reseller_accounts to get all accounts and their balances
2. Call esim_status_per_account for each account to get SIM status breakdowns
3. Analyze and present:
   - Total eSIMs by status (active, suspended, inventory)
   - Utilization rate (active / total provisioned)
   - Accounts with low balance (< $50) — flag as urgent
   - Accounts with high inactive SIM ratio — flag for cleanup
   - Top accounts by active SIM count

Format as a structured report with sections, tables, and actionable recommendations.`,
          },
        },
      ],
    }),
  );

  server.registerPrompt(
    "subscriber_deep_dive",
    {
      title: "Subscriber Deep Dive",
      description:
        "Comprehensive analysis of a single subscriber: profile, packages, usage patterns, location history, recommendations.",
      argsSchema: { iccid: z.string().describe("The subscriber ICCID to analyse") },
    },
    async ({ iccid }) => ({
      messages: [
        {
          role: "user",
          content: {
            type: "text",
            text: `You are a Carrier customer success analyst. Deep-dive into subscriber ${iccid}:

1. Call get_subscriber with ICCID "${iccid}" for profile details
2. Call list_subscriber_packages for their active/expired packages
3. Call subscriber_usage for the last 7 days to see usage patterns
4. Call subscriber_network_events for the last 7 days for connectivity
5. Call get_subscriber_location for current location

Analyze and present:
- Subscriber profile summary (status, account, balance, contact)
- Package utilization: % of data/voice/SMS used vs allowance
- Usage trends: increasing/decreasing/stable
- Roaming behavior: which countries/networks
- Connectivity quality: attach/detach frequency
- Actionable recommendations:
  - If usage > 80% of allowance → suggest upgrade
  - If usage < 20% → suggest downgrade to prevent churn
  - If frequent network switches → check steering list
  - If low balance → alert for top-up`,
          },
        },
      ],
    }),
  );

  server.registerPrompt(
    "revenue_optimization",
    {
      title: "Revenue Optimization Analysis",
      description:
        "Analyse accounts and subscribers to find revenue optimization opportunities: underutilized packages, upgrade candidates, churn risks.",
    },
    async () => ({
      messages: [
        {
          role: "user",
          content: {
            type: "text",
            text: `You are a Carrier revenue optimization analyst. Find opportunities by:

1. Call list_reseller_accounts for account overview
2. Call list_package_templates to understand the product catalog
3. Call get_tariff to understand the cost structure
4. For top accounts, call list_subscribers and sample subscriber_usage

Analyze and present:
- Package template analysis: which templates are most/least popular
- Pricing gap analysis: cost vs retail price margins
- Upgrade candidates: subscribers consistently hitting limits
- Downgrade/churn risks: subscribers with declining usage
- Geographic opportunities: high-usage zones with limited coverage
- Recommendations ranked by estimated revenue impact`,
          },
        },
      ],
    }),
  );

  server.registerPrompt(
    "coverage_analysis",
    {
      title: "Coverage & Network Analysis",
      description:
        "Analyse network coverage, steering lists, and subscriber roaming patterns to optimise connectivity and reduce costs.",
    },
    async () => ({
      messages: [
        {
          role: "user",
          content: {
            type: "text",
            text: `You are a Carrier network coverage analyst. Analyze the fleet's connectivity by:

1. Call list_steering_lists to see network steering configurations
2. Call list_detailed_location_zones for coverage zone definitions
3. Call list_sponsors for available sponsor networks
4. Call list_network_profiles for connectivity configs
5. Sample subscriber_network_events for active subscribers

Analyze and present:
- Coverage map: which zones/countries are covered
- Steering list effectiveness: are subs connecting to preferred networks?
- Roaming cost hotspots: countries with high roaming fees
- Network quality: attach/detach patterns by network
- Recommendations for steering list optimization`,
          },
        },
      ],
    }),
  );

  server.registerPrompt(
    "bulk_operations_planner",
    {
      title: "Bulk Operations Planner",
      description:
        "Plan bulk operations safely: mass package assignments, account migrations, balance adjustments, or status changes.",
      argsSchema: { operation: z.string().describe("Describe the bulk operation you want to perform") },
    },
    async ({ operation }) => ({
      messages: [
        {
          role: "user",
          content: {
            type: "text",
            text: `You are a Carrier operations planner. The user wants to perform this bulk operation:

"${operation}"

Create a safe execution plan:
1. First, use read-only tools to understand the current state
2. Identify all affected subscribers/accounts
3. Estimate the impact (cost, service disruption, reversibility)
4. Generate a step-by-step plan with:
   - Pre-flight checks
   - Execution order (smallest batch first as canary)
   - Rollback procedure for each step
   - Post-execution verification
5. List all destructive tool calls that will be needed
6. Ask for explicit confirmation before any destructive action

NEVER execute destructive operations without confirmation.`,
          },
        },
      ],
    }),
  );
}
